import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// 이미지 압축 설정
const IMAGE_COMPRESSION_CONFIG = {
  maxSizeKB: 800, // 최대 파일 크기 (KB) - Nginx 1MB 제한 대응
  maxWidth: 1920, // 최대 너비
  maxHeight: 1920, // 최대 높이
  quality: 0.85, // JPEG 품질 (0.0 - 1.0)
};

// 이미지 압축 함수 (iOS Safari 호환)
const compressImage = async (file, config = IMAGE_COMPRESSION_CONFIG) => {
  // 이미지가 아니면 원본 반환
  if (!file.type.startsWith('image/') && !file.name.toLowerCase().match(/\.(heic|heif)$/)) {
    return file;
  }

  // 이미 충분히 작으면 원본 반환
  if (file.size < config.maxSizeKB * 1024) {
    console.log(`Image ${file.name} is already small enough (${Math.round(file.size/1024)}KB)`);
    return file;
  }

  console.log(`Compressing image ${file.name} (${Math.round(file.size/1024)}KB)...`);

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          // 리사이즈 비율 계산
          let { width, height } = img;
          const ratio = Math.min(
            config.maxWidth / width,
            config.maxHeight / height,
            1 // 원본보다 커지지 않도록
          );

          width = Math.round(width * ratio);
          height = Math.round(height * ratio);

          // Canvas에 그리기
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // 새 파일 이름
          const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg').replace(/\.[^.]+$/, '.jpg');

          // iOS Safari 호환: toBlob 대신 toDataURL 사용 (더 안정적)
          const compressWithQuality = (quality) => {
            try {
              const dataUrl = canvas.toDataURL('image/jpeg', quality);

              // DataURL을 Blob으로 변환
              const byteString = atob(dataUrl.split(',')[1]);
              const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
              const ab = new ArrayBuffer(byteString.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
              }
              const blob = new Blob([ab], { type: mimeString });

              const compressedSizeKB = Math.round(blob.size / 1024);
              console.log(`Compressed to ${compressedSizeKB}KB (quality: ${quality.toFixed(2)})`);

              // 여전히 너무 크면 품질 더 낮추기
              if (blob.size > config.maxSizeKB * 1024 && quality > 0.3) {
                compressWithQuality(quality - 0.1);
                return;
              }

              console.log(`Final: ${file.name} (${Math.round(file.size/1024)}KB) → ${newFileName} (${compressedSizeKB}KB)`);

              // Blob 반환 (iOS Safari에서 File보다 안정적)
              blob.name = newFileName;
              resolve(blob);
            } catch (err) {
              console.error('Compression error:', err);
              resolve(file); // 실패시 원본 반환
            }
          };

          compressWithQuality(config.quality);
        } catch (err) {
          console.error('Canvas error:', err);
          resolve(file); // 실패시 원본 반환
        }
      };
      img.onerror = () => {
        console.error('Failed to load image for compression');
        resolve(file); // 실패시 원본 반환
      };
      img.src = e.target.result;
    };
    reader.onerror = () => {
      console.error('Failed to read file for compression');
      resolve(file); // 실패시 원본 반환
    };
    reader.readAsDataURL(file);
  });
};

const FileUpload = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [serverFiles, setServerFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'server'
  const [selectedUploaded, setSelectedUploaded] = useState(new Set());
  const [selectedServer, setSelectedServer] = useState(new Set());
  const [currentFolder, setCurrentFolder] = useState('');
  const [viewMode, setViewMode] = useState('folder'); // 'grid' | 'folder'
  const [expandedFolders, setExpandedFolders] = useState(new Set(['기본']));
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // 서버 파일 목록 로드
  const loadServerFiles = useCallback(async () => {
    try {
      const response = await api.get('/api/files/list');
      setServerFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load server files:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServerFiles();
  }, [loadServerFiles]);

  const getFileIcon = (type, name) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return '📊';
    if (type?.includes('powerpoint') || type?.includes('presentation')) return '📽️';
    if (type === 'application/zip' || type === 'application/x-zip-compressed' ||
        name?.endsWith('.zip')) return '📦';
    if (type === 'application/x-rar-compressed' || name?.endsWith('.rar')) return '📦';
    return '📁';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(date));
  };

  const formatDate = (dateStr) => {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateStr));
  };

  const processAndUploadFiles = useCallback(async (files) => {
    if (!files || files.length === 0) {
      console.log('No files to process');
      return;
    }

    console.log('Processing', files.length, 'files');
    setIsUploading(true);
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      console.log('Processing file:', file.name, 'Type:', file.type, 'Size:', file.size);

      // 폴더 경로 추출 (webkitRelativePath가 있는 경우)
      const folderPath = file.webkitRelativePath
        ? file.webkitRelativePath.split('/').slice(0, -1).join('/')
        : currentFolder;

      const fileData = {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        uploadedAt: new Date(),
        preview: null,
        status: 'uploading',
        file: file,
        serverSaved: false,
        folder: folderPath,
        selected: false,
      };

      // Add file to list immediately with uploading status
      setUploadedFiles((prev) => [fileData, ...prev]);

      // Generate preview for images
      if (file.type && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === fileData.id ? { ...f, preview: e.target.result } : f
            )
          );
        };
        reader.onerror = (e) => {
          console.error('FileReader error for', file.name, e);
        };
        reader.readAsDataURL(file);
      }

      // Simulate processing
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Update status to completed
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id ? { ...f, status: 'completed' } : f
        )
      );
    }

    setIsUploading(false);
    console.log('All files processed');
  }, [currentFolder]);

  // 서버에 저장
  const handleSaveToServer = async (fileData) => {
    try {
      console.log('Saving to server:', fileData.name);
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id ? { ...f, status: 'compressing' } : f
        )
      );

      // 이미지인 경우 압축
      let fileToUpload = fileData.file;
      if (fileData.file.type?.startsWith('image/') || fileData.name.toLowerCase().match(/\.(heic|heif)$/)) {
        console.log('Compressing image before upload...');
        fileToUpload = await compressImage(fileData.file);
      }

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id ? { ...f, status: 'saving' } : f
        )
      );

      const formData = new FormData();
      // iOS Safari 호환성: 파일명을 명시적으로 지정
      const fileName = fileToUpload.name || fileData.name || 'image.jpg';
      formData.append('file', fileToUpload, fileName);
      if (fileData.folder) {
        formData.append('folder', fileData.folder);
      }

      console.log('FormData prepared, sending to server...',
        `Original: ${Math.round(fileData.file.size/1024)}KB, Compressed: ${Math.round(fileToUpload.size/1024)}KB, FileName: ${fileName}`);

      const response = await api.post('/api/files/upload', formData, {
        // Don't set Content-Type for FormData - browser will set it automatically with boundary
        timeout: 120000, // 120 seconds timeout for mobile uploads
      });

      console.log('Server response:', response.data);

      if (response.data.success) {
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? { ...f, status: 'saved', serverSaved: true, serverId: response.data.file.id }
              : f
          )
        );
        // 서버 파일 목록 새로고침
        loadServerFiles();
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Save to server failed:', error);
      const errorMessage = error.response?.data?.error || error.message || '서버 저장 실패';
      alert(`파일 저장 실패: ${errorMessage}`);
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id ? { ...f, status: 'error' } : f
        )
      );
    }
  };

  // 서버에서 삭제
  const handleDeleteFromServer = async (fileId) => {
    try {
      await api.delete(`/api/files/${fileId}`);
      setServerFiles((prev) => prev.filter((f) => f.id !== fileId));
      setSelectedServer((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    } catch (error) {
      console.error('Delete from server failed:', error);
      alert('파일 삭제 실패');
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const items = e.dataTransfer.items;
    const files = [];

    // 폴더 드래그 처리
    if (items) {
      const processEntry = async (entry, path = '') => {
        if (entry.isFile) {
          return new Promise((resolve) => {
            entry.file((file) => {
              // webkitRelativePath를 수동으로 설정
              Object.defineProperty(file, 'webkitRelativePath', {
                value: path + file.name,
                writable: false
              });
              files.push(file);
              resolve();
            });
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          return new Promise((resolve) => {
            reader.readEntries(async (entries) => {
              for (const e of entries) {
                await processEntry(e, path + entry.name + '/');
              }
              resolve();
            });
          });
        }
      };

      const processItems = async () => {
        for (let i = 0; i < items.length; i++) {
          const entry = items[i].webkitGetAsEntry?.();
          if (entry) {
            await processEntry(entry);
          }
        }
        if (files.length > 0) {
          processAndUploadFiles(files);
        }
      };

      processItems();
    } else {
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        processAndUploadFiles(droppedFiles);
      }
    }
  }, [processAndUploadFiles]);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      console.log('Files selected:', files.length, 'files');
      processAndUploadFiles(files);
    } else {
      console.log('No files selected');
    }
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleFolderButtonClick = (e) => {
    e.stopPropagation();
    folderInputRef.current?.click();
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setSelectedUploaded((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fileId);
      return newSet;
    });
  };

  const handleClearAll = () => {
    if (uploadedFiles.length > 0 && window.confirm('모든 업로드 파일을 삭제하시겠습니까?')) {
      setUploadedFiles([]);
      setSelectedUploaded(new Set());
    }
  };

  // 전체 서버 저장
  const handleSaveAllToServer = async () => {
    const filesToSave = uploadedFiles.filter(
      f => f.status === 'completed' && !f.serverSaved && f.file
    );

    if (filesToSave.length === 0) {
      alert('저장할 파일이 없습니다.');
      return;
    }

    for (const fileData of filesToSave) {
      await handleSaveToServer(fileData);
    }
  };

  // 선택 토글 (업로드)
  const toggleUploadedSelection = (fileId) => {
    setSelectedUploaded((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // 선택 토글 (서버)
  const toggleServerSelection = (fileId) => {
    setSelectedServer((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // 전체 선택 (업로드)
  const selectAllUploaded = () => {
    if (selectedUploaded.size === uploadedFiles.length) {
      setSelectedUploaded(new Set());
    } else {
      setSelectedUploaded(new Set(uploadedFiles.map(f => f.id)));
    }
  };

  // 전체 선택 (서버)
  const selectAllServer = () => {
    if (selectedServer.size === serverFiles.length) {
      setSelectedServer(new Set());
    } else {
      setSelectedServer(new Set(serverFiles.map(f => f.id)));
    }
  };

  // 선택 삭제 (업로드)
  const deleteSelectedUploaded = () => {
    if (selectedUploaded.size === 0) return;
    if (window.confirm(`선택한 ${selectedUploaded.size}개 파일을 삭제하시겠습니까?`)) {
      setUploadedFiles((prev) => prev.filter(f => !selectedUploaded.has(f.id)));
      setSelectedUploaded(new Set());
    }
  };

  // 선택 삭제 (서버)
  const deleteSelectedServer = async () => {
    if (selectedServer.size === 0) return;
    if (window.confirm(`선택한 ${selectedServer.size}개 파일을 서버에서 삭제하시겠습니까?`)) {
      for (const fileId of selectedServer) {
        await handleDeleteFromServer(fileId);
      }
    }
  };

  // 전체 삭제 (서버)
  const deleteAllServer = async () => {
    if (serverFiles.length === 0) return;
    if (window.confirm(`서버의 모든 파일(${serverFiles.length}개)을 삭제하시겠습니까?`)) {
      for (const file of serverFiles) {
        await handleDeleteFromServer(file.id);
      }
    }
  };

  // 선택 저장 (서버로)
  const saveSelectedToServer = async () => {
    const filesToSave = uploadedFiles.filter(
      f => selectedUploaded.has(f.id) && f.status === 'completed' && !f.serverSaved && f.file
    );

    if (filesToSave.length === 0) {
      alert('저장할 파일이 없습니다.');
      return;
    }

    for (const fileData of filesToSave) {
      await handleSaveToServer(fileData);
    }
    setSelectedUploaded(new Set());
  };

  // 저장 가능한 파일 수
  const pendingFilesCount = uploadedFiles.filter(
    f => f.status === 'completed' && !f.serverSaved
  ).length;

  // 폴더별 그룹화
  const groupedServerFiles = serverFiles.reduce((acc, file) => {
    const folder = file.folder || '기본';
    if (!acc[folder]) acc[folder] = [];
    acc[folder].push(file);
    return acc;
  }, {});

  const folders = Object.keys(groupedServerFiles).sort((a, b) => {
    if (a === '기본') return -1;
    if (b === '기본') return 1;
    return a.localeCompare(b);
  });

  // 폴더 토글
  const toggleFolder = (folder) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folder)) {
        newSet.delete(folder);
      } else {
        newSet.add(folder);
      }
      return newSet;
    });
  };

  // 폴더 전체 펼치기/접기
  const toggleAllFolders = () => {
    if (expandedFolders.size === folders.length) {
      setExpandedFolders(new Set());
    } else {
      setExpandedFolders(new Set(folders));
    }
  };

  // 폴더 내 파일 전체 선택
  const selectFolderFiles = (folder) => {
    const folderFiles = groupedServerFiles[folder] || [];
    const folderFileIds = folderFiles.map(f => f.id);
    const allSelected = folderFileIds.every(id => selectedServer.has(id));

    setSelectedServer((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        folderFileIds.forEach(id => newSet.delete(id));
      } else {
        folderFileIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  return (
    <div className="max-w-[1000px] mx-auto py-12 px-6 font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Display','SF_Pro_Text','Helvetica_Neue',sans-serif]">
      <header className="text-center mb-12">
        <h1 className="text-5xl font-bold tracking-tight text-[#1d1d1f] mb-3">{t('fileUpload.title')}</h1>
        <p className="text-xl text-[#86868b] font-normal">{t('fileUpload.subtitle')}</p>
      </header>

      {/* 탭 네비게이션 */}
      <div className="flex gap-2 mb-6 bg-[#f5f5f7] rounded-xl p-1">
        <button
          className={`flex-1 py-3 px-4 border-none rounded-[10px] text-[0.95rem] font-medium cursor-pointer transition-all duration-200 min-h-[44px] ${activeTab === 'upload' ? 'bg-white text-[#1d1d1f] shadow-md' : 'bg-transparent text-[#86868b] hover:text-[#1d1d1f]'}`}
          onClick={() => setActiveTab('upload')}
        >
          📤 파일 업로드
        </button>
        <button
          className={`flex-1 py-3 px-4 border-none rounded-[10px] text-[0.95rem] font-medium cursor-pointer transition-all duration-200 min-h-[44px] ${activeTab === 'server' ? 'bg-white text-[#1d1d1f] shadow-md' : 'bg-transparent text-[#86868b] hover:text-[#1d1d1f]'}`}
          onClick={() => setActiveTab('server')}
        >
          🗂️ 서버 파일 ({serverFiles.length})
        </button>
      </div>

      <div className="flex flex-col gap-8">
        {activeTab === 'upload' && (
          <>
            {/* Drop Zone */}
            <div
              className={`bg-gradient-to-br from-[#f5f5f7] to-white rounded-3xl py-16 px-12 cursor-pointer transition-all duration-400 relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:border-2 before:border-dashed before:border-[#d2d2d7] before:rounded-3xl before:pointer-events-none before:transition-all before:duration-300 hover:scale-[1.01] hover:shadow-lg hover:before:border-[#0071e3] ${isDragOver ? 'bg-gradient-to-br from-[#e3f2ff] to-[#f0f7ff] scale-[1.02] shadow-xl before:border-[#0071e3] before:border-[3px]' : ''} ${isUploading ? 'pointer-events-none' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleDropzoneClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.heic,.heif,audio/*,.mp3,.m4a,.aac,.wav,.caf,.ogg,video/*,.mp4,.mov,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/zip,application/x-zip-compressed,application/x-rar-compressed"
              />
              <input
                ref={folderInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                {...(typeof window !== 'undefined' && 'webkitdirectory' in document.createElement('input') ? { webkitdirectory: '' } : {})}
              />
              <div className="flex flex-col items-center gap-4">
                {isUploading ? (
                  <>
                    <div className="w-12 h-12 border-[3px] border-[#e8e8ed] border-t-[#0071e3] rounded-full animate-spin"></div>
                    <p className="text-xl font-semibold text-[#1d1d1f] m-0">{t('fileUpload.dropzone.uploading')}</p>
                  </>
                ) : (
                  <>
                    <div className="text-[#86868b] transition-all duration-300 hover:text-[#0071e3] hover:-translate-y-1">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    </div>
                    <p className="text-xl font-semibold text-[#1d1d1f] m-0">파일을 선택하거나 드래그하세요</p>
                    <p className="text-[0.95rem] text-[#86868b] m-0">이미지, 문서, ZIP 압축파일 지원</p>
                    <div className="flex gap-4 mt-6 flex-wrap justify-center">
                      <button
                        type="button"
                        className="py-3 px-6 border-none rounded-full text-[0.95rem] font-semibold cursor-pointer transition-all duration-300 min-h-[44px] min-w-[120px] bg-gradient-to-br from-[#0071e3] to-[#00c7be] text-white shadow-lg hover:scale-105 hover:shadow-xl"
                        onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      >
                        📄 파일 선택
                      </button>
                      <button
                        type="button"
                        className="py-3 px-6 border-none rounded-full text-[0.95rem] font-semibold cursor-pointer transition-all duration-300 min-h-[44px] min-w-[120px] bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] text-[#1d1d1f] shadow-lg hover:scale-105 hover:shadow-xl"
                        onClick={handleFolderButtonClick}
                      >
                        📁 폴더 선택
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* 폴더 경로 입력 (선택적) */}
            <div className="flex items-center gap-4 py-3 px-4 bg-[#f5f5f7] rounded-xl flex-wrap">
              <label className="text-[0.9rem] font-medium text-[#86868b] whitespace-nowrap">📁 저장 폴더:</label>
              <input
                type="text"
                value={currentFolder}
                onChange={(e) => setCurrentFolder(e.target.value)}
                placeholder="폴더명 입력 (예: 화투/1월)"
                className="flex-1 py-2 px-4 border border-[#e5e5e5] rounded-lg text-[0.9rem] bg-white transition-all duration-200 focus:outline-none focus:border-[#0071e3] focus:shadow-[0_0_0_3px_rgba(0,113,227,0.1)]"
              />
            </div>

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#f5f5f7] flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUploaded.size === uploadedFiles.length && uploadedFiles.length > 0}
                        onChange={selectAllUploaded}
                        className="w-[18px] h-[18px] accent-[#0071e3] cursor-pointer"
                      />
                      <span className="text-[0.9rem] font-medium text-[#86868b]">전체 선택</span>
                    </label>
                    <h2 className="text-2xl font-semibold text-[#1d1d1f] m-0">업로드된 파일 ({uploadedFiles.length})</h2>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    {selectedUploaded.size > 0 && (
                      <>
                        <button className="py-2 px-4 bg-gradient-to-br from-[#34c759] to-[#30d158] border-none rounded-full text-white text-[0.85rem] font-semibold cursor-pointer transition-all duration-200 shadow-md hover:scale-[1.02] hover:shadow-lg" onClick={saveSelectedToServer}>
                          💾 선택 저장 ({selectedUploaded.size})
                        </button>
                        <button className="py-2 px-4 bg-gradient-to-br from-[#ff3b30] to-[#ff453a] border-none rounded-full text-white text-[0.85rem] font-semibold cursor-pointer transition-all duration-200 shadow-md hover:scale-[1.02] hover:shadow-lg" onClick={deleteSelectedUploaded}>
                          🗑️ 선택 삭제
                        </button>
                      </>
                    )}
                    {pendingFilesCount > 0 && (
                      <button className="py-2 px-4 bg-gradient-to-br from-[#0071e3] to-[#00c7be] border-none rounded-full text-white text-[0.85rem] font-semibold cursor-pointer transition-all duration-200 shadow-md hover:scale-[1.02] hover:shadow-lg" onClick={handleSaveAllToServer}>
                        💾 전체 저장 ({pendingFilesCount})
                      </button>
                    )}
                    <button className="py-2 px-4 bg-[#f5f5f7] border-none rounded-full text-[#86868b] text-[0.85rem] font-medium cursor-pointer transition-all duration-200 hover:bg-[#e8e8ed] hover:text-[#1d1d1f]" onClick={handleClearAll}>
                      전체 삭제
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className={`rounded-2xl p-5 transition-all duration-300 ${file.status === 'uploading' || file.status === 'analyzing' ? 'bg-[#fff8e6]' : file.status === 'completed' ? 'bg-[#f0f9f4]' : 'bg-[#fbfbfd] hover:bg-[#f5f5f7]'} ${selectedUploaded.has(file.id) ? 'bg-[#e3f2ff] border-2 border-[#0071e3]' : ''}`}>
                      <div className="flex items-center gap-4 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedUploaded.has(file.id)}
                            onChange={() => toggleUploadedSelection(file.id)}
                            className="w-[18px] h-[18px] accent-[#0071e3] cursor-pointer"
                          />
                        </label>
                        <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
                          {file.preview ? (
                            <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-2xl">{getFileIcon(file.type, file.name)}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-[#1d1d1f] m-0 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">{file.name}</h3>
                          <div className="text-[0.8rem] text-[#86868b] flex items-center gap-2">
                            <span>{formatFileSize(file.size)}</span>
                            <span className="opacity-50">•</span>
                            <span>{formatTime(file.uploadedAt)}</span>
                            {file.folder && (
                              <>
                                <span className="opacity-50">•</span>
                                <span className="inline-flex items-center gap-1 py-[0.15rem] px-2 bg-[#e3f2ff] text-[#0071e3] text-xs font-medium rounded-md">📁 {file.folder}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {file.status === 'completed' && !file.serverSaved && (
                            <button
                              className="py-2 px-4 bg-gradient-to-br from-[#0071e3] to-[#00c7be] text-white border-none rounded-[20px] text-[0.85rem] font-medium cursor-pointer transition-all duration-200 min-h-[36px] hover:scale-105 hover:shadow-lg"
                              onClick={() => handleSaveToServer(file)}
                            >
                              💾 저장
                            </button>
                          )}
                          {file.status === 'compressing' && (
                            <span className="text-[#ff9500] text-[0.85rem] font-medium">압축 중...</span>
                          )}
                          {file.status === 'saving' && (
                            <span className="text-[#0071e3] text-[0.85rem] font-medium">저장 중...</span>
                          )}
                          {file.status === 'saved' && (
                            <span className="text-[#34c759] text-[0.85rem] font-medium">✅ 저장됨</span>
                          )}
                          {file.status === 'error' && (
                            <span className="text-[#ff3b30] text-[0.85rem] font-medium">❌ 오류</span>
                          )}
                          <button
                            className="w-8 h-8 border-none bg-transparent text-[#86868b] text-2xl cursor-pointer rounded-full flex items-center justify-center transition-all duration-200 hover:bg-[#ffebee] hover:text-[#ff3b30]"
                            onClick={() => handleRemoveFile(file.id)}
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {file.preview && file.status !== 'uploading' && (
                        <div className="mt-4 rounded-xl overflow-hidden max-h-[300px]">
                          <img src={file.preview} alt={file.name} className="w-full h-auto max-h-[300px] object-contain bg-[#f5f5f7]" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadedFiles.length === 0 && (
              <div className="text-center py-12 px-8 bg-[#fbfbfd] rounded-2xl">
                <p className="text-[#86868b] text-base m-0">{t('fileUpload.analysis.waiting')}</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'server' && (
          <div className="min-h-[300px]">
            {/* 서버 파일 헤더 */}
            <div className="flex justify-between items-center mb-6 p-4 bg-[#f5f5f7] rounded-2xl flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedServer.size === serverFiles.length && serverFiles.length > 0}
                    onChange={selectAllServer}
                    className="w-[18px] h-[18px] accent-[#0071e3] cursor-pointer"
                  />
                  <span className="text-[0.9rem] font-medium text-[#86868b]">전체 선택</span>
                </label>
                <span className="text-[0.9rem] font-medium text-[#86868b]">{serverFiles.length}개 파일 • {folders.length}개 폴더</span>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex bg-[#e8e8ed] rounded-lg p-[2px]">
                  <button
                    className={`py-[0.4rem] px-3 border-none text-base cursor-pointer rounded-md transition-all duration-200 ${viewMode === 'folder' ? 'bg-white shadow-md' : 'bg-transparent hover:bg-white/50'}`}
                    onClick={() => setViewMode('folder')}
                    title="폴더 보기"
                  >
                    📁
                  </button>
                  <button
                    className={`py-[0.4rem] px-3 border-none text-base cursor-pointer rounded-md transition-all duration-200 ${viewMode === 'grid' ? 'bg-white shadow-md' : 'bg-transparent hover:bg-white/50'}`}
                    onClick={() => setViewMode('grid')}
                    title="그리드 보기"
                  >
                    ⊞
                  </button>
                </div>
                {viewMode === 'folder' && folders.length > 1 && (
                  <button className="py-2 px-4 bg-[#f5f5f7] border-none rounded-full text-[#1d1d1f] text-[0.85rem] font-medium cursor-pointer transition-all duration-200 hover:bg-[#e8e8ed]" onClick={toggleAllFolders}>
                    {expandedFolders.size === folders.length ? '📁 모두 접기' : '📂 모두 펼치기'}
                  </button>
                )}
                {selectedServer.size > 0 && (
                  <button className="py-2 px-4 bg-gradient-to-br from-[#ff3b30] to-[#ff453a] border-none rounded-full text-white text-[0.85rem] font-semibold cursor-pointer transition-all duration-200 shadow-md hover:scale-[1.02] hover:shadow-lg" onClick={deleteSelectedServer}>
                    🗑️ 선택 삭제 ({selectedServer.size})
                  </button>
                )}
                {serverFiles.length > 0 && (
                  <button className="py-2 px-4 bg-[#ffebee] border-none rounded-full text-[#ff3b30] text-[0.85rem] font-medium cursor-pointer transition-all duration-200 hover:bg-[#ffcdd2]" onClick={deleteAllServer}>
                    🗑️ 전체 삭제
                  </button>
                )}
                <button className="py-2 px-4 bg-[#f5f5f7] border-none rounded-full text-[#1d1d1f] text-[0.85rem] font-medium cursor-pointer transition-all duration-200 hover:bg-[#e8e8ed]" onClick={loadServerFiles}>
                  🔄
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-4 py-16 px-8 text-[#86868b]">
                <div className="w-12 h-12 border-[3px] border-[#e8e8ed] border-t-[#0071e3] rounded-full animate-spin"></div>
                <p>파일 목록 로딩 중...</p>
              </div>
            ) : serverFiles.length === 0 ? (
              <div className="text-center py-12 px-8 bg-[#fbfbfd] rounded-2xl">
                <p className="text-[#86868b] text-base m-0">서버에 저장된 파일이 없습니다.</p>
              </div>
            ) : viewMode === 'folder' ? (
              /* 폴더 뷰 */
              <div className="flex flex-col gap-4">
                {folders.map((folder) => {
                  const folderFiles = groupedServerFiles[folder];
                  const isExpanded = expandedFolders.has(folder);
                  const folderFileIds = folderFiles.map(f => f.id);
                  const allSelected = folderFileIds.length > 0 && folderFileIds.every(id => selectedServer.has(id));
                  const someSelected = folderFileIds.some(id => selectedServer.has(id));

                  return (
                    <div key={folder} className="bg-white rounded-2xl overflow-hidden shadow-md">
                      <div className="flex justify-between items-center py-4 px-5 bg-gradient-to-br from-[#f8f9fa] to-[#f0f0f5] cursor-pointer transition-all duration-200 hover:from-[#f0f0f5] hover:to-[#e8e8ed]" onClick={() => toggleFolder(folder)}>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-[#86868b] transition-transform duration-300">{isExpanded ? '▼' : '▶'}</span>
                          <span className="text-2xl">{isExpanded ? '📂' : '📁'}</span>
                          <span className="text-lg font-semibold text-[#1d1d1f]">{folder}</span>
                          <span className="text-[0.85rem] text-[#86868b] bg-[#e8e8ed] py-[0.2rem] px-[0.6rem] rounded-full">{folderFiles.length}개 파일</span>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <label className="cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected && !allSelected;
                              }}
                              onChange={() => selectFolderFiles(folder)}
                              className="w-5 h-5 accent-[#0071e3] cursor-pointer"
                            />
                          </label>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 p-4 bg-[#fbfbfd]">
                          {folderFiles.map((file, index) => (
                            <div
                              key={file.id}
                              className={`bg-white rounded-xl overflow-hidden shadow-md transition-all duration-300 relative hover:-translate-y-1 hover:shadow-lg ${selectedServer.has(file.id) ? 'border-[3px] border-[#0071e3] shadow-lg' : ''}`}
                            >
                              <label className="absolute top-3 right-3 z-[15] flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedServer.has(file.id)}
                                  onChange={() => toggleServerSelection(file.id)}
                                  className="w-5 h-5 bg-white rounded shadow-md accent-[#0071e3] cursor-pointer"
                                />
                              </label>
                              <div className="h-[120px] bg-[#f5f5f7] flex items-center justify-center overflow-hidden">
                                {file.mimeType?.startsWith('image/') ? (
                                  <img
                                    src={`https://api.ilouli.com${file.url}`}
                                    alt={file.originalName}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-5xl">{getFileIcon(file.mimeType, file.originalName)}</span>
                                )}
                              </div>
                              <div className="p-3">
                                <h3 className="text-[0.85rem] font-semibold text-[#1d1d1f] m-0 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">{file.originalName}</h3>
                                <p className="text-xs text-[#86868b] m-0">
                                  {formatFileSize(file.size)}
                                </p>
                              </div>
                              <div className="flex gap-[0.35rem] px-3 pb-3">
                                <a
                                  href={`https://api.ilouli.com${file.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 py-[0.4rem] border-none rounded-lg text-[0.9rem] font-medium cursor-pointer transition-all duration-200 text-center no-underline min-h-[44px] flex items-center justify-center bg-[#f5f5f7] text-[#1d1d1f] hover:bg-[#e8e8ed]"
                                >
                                  👁️
                                </a>
                                <button
                                  className="flex-1 py-[0.4rem] border-none rounded-lg text-[0.9rem] font-medium cursor-pointer transition-all duration-200 text-center min-h-[44px] flex items-center justify-center bg-[#ffebee] text-[#ff3b30] hover:bg-[#ffcdd2]"
                                  onClick={() => handleDeleteFromServer(file.id)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* 그리드 뷰 */
              <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
                {serverFiles.map((file, index) => (
                  <div
                    key={file.id}
                    className={`bg-white rounded-2xl overflow-hidden shadow-lg transition-all duration-300 relative hover:-translate-y-1 hover:shadow-xl ${selectedServer.has(file.id) ? 'border-[3px] border-[#0071e3] shadow-xl' : ''}`}
                  >
                    <label className="absolute top-3 right-3 z-[15] flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedServer.has(file.id)}
                        onChange={() => toggleServerSelection(file.id)}
                        className="w-5 h-5 bg-white rounded shadow-md accent-[#0071e3] cursor-pointer"
                      />
                    </label>
                    <div className="absolute top-3 left-3 bg-gradient-to-br from-[#0071e3] to-[#00c7be] text-white text-[0.85rem] font-bold py-1 px-[10px] rounded-[20px] z-10 shadow-md">#{index + 1}</div>
                    <div className="h-[180px] bg-[#f5f5f7] flex items-center justify-center overflow-hidden">
                      {file.mimeType?.startsWith('image/') ? (
                        <img
                          src={`https://api.ilouli.com${file.url}`}
                          alt={file.originalName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-7xl">{getFileIcon(file.mimeType, file.originalName)}</span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-[#1d1d1f] m-0 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">{file.originalName}</h3>
                      <p className="text-[0.8rem] text-[#86868b] m-0">
                        {formatFileSize(file.size)} • {formatDate(file.uploadedAt)}
                      </p>
                      {file.folder && (
                        <p className="inline-flex items-center gap-1 py-[0.15rem] px-2 bg-[#e3f2ff] text-[#0071e3] text-xs font-medium rounded-md mt-1">📁 {file.folder}</p>
                      )}
                    </div>
                    <div className="flex gap-2 px-4 pb-4">
                      <a
                        href={`https://api.ilouli.com${file.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-[0.6rem] border-none rounded-lg text-[0.85rem] font-medium cursor-pointer transition-all duration-200 text-center no-underline min-h-[44px] flex items-center justify-center bg-[#f5f5f7] text-[#1d1d1f] hover:bg-[#e8e8ed]"
                      >
                        👁️ 보기
                      </a>
                      <button
                        className="flex-1 py-[0.6rem] border-none rounded-lg text-[0.85rem] font-medium cursor-pointer transition-all duration-200 text-center min-h-[44px] flex items-center justify-center bg-[#ffebee] text-[#ff3b30] hover:bg-[#ffcdd2]"
                        onClick={() => handleDeleteFromServer(file.id)}
                      >
                        🗑️ 삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
